import './App.css';
import React, { useState, useRef, useEffect } from 'react';
import { EyeIcon, RefreshIcon, BackIcon } from './Icons';
import { shuffle } from './utils';

export default function FlashcardsApp({ words, onBack }) {
  const [shuffledWords, setShuffledWords] = useState(() => shuffle(words));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState({});
  const [completed, setCompleted] = useState(false);
  const [toRepeat, setToRepeat] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);
  const [isHardMode, setIsHardMode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const timerRef = useRef(null);
  const handleTimeoutRef = useRef(null);

  const currentWord = shuffledWords[currentIndex];
  const mistakeCount = mistakes[currentIndex] || 0;

  const checkAnswer = () => {
    if (isHardMode) clearInterval(timerRef.current);

    const input = userInput.trim().toLowerCase();
    const correct = currentWord.de.toLowerCase();

    if (input === correct) {
      setIsCorrect(true);
      setTimeout(() => {
        setIsCorrect(null);
        setUserInput('');
        setShowHint(false);
        nextWord();
        inputRef.current?.focus();
      }, 800);
    } else {
      setIsCorrect(false);
      const newMistakes = { ...mistakes };
      newMistakes[currentIndex] = (newMistakes[currentIndex] || 0) + 1;
      setMistakes(newMistakes);
      setScore(s => s + 1);

      if (newMistakes[currentIndex] >= 5) {
        setTimeout(() => {
          setIsCorrect(null);
          setUserInput('');
          setShowHint(false);
          nextWord();
          inputRef.current?.focus();
        }, 1000);
      }
    }
  };

  const nextWord = () => {
    if (currentIndex + 1 < shuffledWords.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      const repeat = Object.entries(mistakes)
        .filter(([_, count]) => count >= 5)
        .map(([index]) => shuffledWords[Number(index)].pl);
      setToRepeat(repeat);
      setCompleted(true);
    }
  };

  const restart = () => {
    setShuffledWords(shuffle(words));
    setCurrentIndex(0);
    setUserInput('');
    setIsCorrect(null);
    setScore(0);
    setMistakes({});
    setCompleted(false);
    setToRepeat([]);
    setShowHint(false);
  };

  const handleTimeout = () => {
    setIsCorrect(false);
    setMistakes(prev => {
      const updated = { ...prev };
      updated[currentIndex] = (updated[currentIndex] || 0) + 1;
      return updated;
    });
    setScore(s => s + 1);

    setTimeout(() => {
      setIsCorrect(null);
      setUserInput('');
      setShowHint(false);
      nextWord();
      inputRef.current?.focus();
    }, 1000);
  };

  const handleSpecialCharClick = (char) => {
    setUserInput(prev => prev + char);
    inputRef.current?.focus();
  };

  const handleArticleClick = (article) => {
    setUserInput(prev => `${article} ${prev.trim()}`);
    inputRef.current?.focus();
  };

  useEffect(() => { inputRef.current?.focus(); }, [currentIndex]);

  handleTimeoutRef.current = handleTimeout;

  useEffect(() => {
    if (!isHardMode || completed) { clearInterval(timerRef.current); return; }
    setTimeLeft(10);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentIndex, isHardMode, completed]);

  useEffect(() => {
    if (isHardMode && timeLeft === 0 && !completed) handleTimeoutRef.current();
  }, [timeLeft, isHardMode, completed]);

  if (completed) {
    return (
      <>
        <div className="flashcard-container">
          <div className="progress-container">
            <div className="progress-bar" style={{ width: '100%' }} />
          </div>
          <div className="completion-header">
            <div className="completion-tag">Ukończono</div>
            <div className="completion-title">Sprawdź się</div>
          </div>
          <div className="score-block">
            <div className={`score-number ${score === 0 ? 'perfect' : ''}`}>{score}</div>
            <div className="score-label">Punkty karne</div>
          </div>
          {toRepeat.length > 0 ? (
            <>
              <div className="repeat-list-label">Słowa do powtórki ({toRepeat.length})</div>
              <ul>{toRepeat.map((word, i) => <li key={i}>{word}</li>)}</ul>
            </>
          ) : (
            <p className="feedback-correct">Bezbłędnie — doskonale!</p>
          )}
          <button className="btn-primary" onClick={restart}><RefreshIcon /> Powtórz</button>
        </div>
        <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
      </>
    );
  }

  return (
    <>
      <div className={`flashcard-container ${isCorrect === true ? 'correct-answer' : ''} ${isCorrect === false ? 'wrong-answer' : ''}`}>
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${Math.round((currentIndex + 1) / shuffledWords.length * 100)}%` }} />
        </div>
        {isHardMode && (
          <div className={`timer ${timeLeft <= 3 ? 'danger' : ''}`}>{timeLeft}</div>
        )}
        <h1>Sprawdź się</h1>
        <div className="card-counter">{currentIndex + 1} / {shuffledWords.length}</div>
        <div className="word-label">Polskie słowo</div>
        <div className="word-prompt">{currentWord.pl}</div>

        <div className="input-area">
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && checkAnswer()}
            placeholder="Wpisz po niemiecku…"
          />
          <div className="special-chars">
            <button onClick={() => handleSpecialCharClick('ä')}>ä</button>
            <button onClick={() => handleSpecialCharClick('ö')}>ö</button>
            <button onClick={() => handleSpecialCharClick('ü')}>ü</button>
            <button onClick={() => handleSpecialCharClick('ß')}>ß</button>
          </div>
        </div>

        <div className="article-buttons">
          <button onClick={() => handleArticleClick('der')}>der</button>
          <button onClick={() => handleArticleClick('die')}>die</button>
          <button onClick={() => handleArticleClick('das')}>das</button>
        </div>

        {isCorrect === false && <p className="feedback-wrong">Błędna odpowiedź — spróbuj ponownie</p>}
        {isCorrect === true  && <p className="feedback-correct">Poprawnie!</p>}

        <div className="action-buttons-container">
          <button className="btn-primary" onClick={checkAnswer}>Sprawdź</button>
        </div>

        {mistakeCount >= 3 && !showHint && (
          <button className="btn-secondary" onClick={() => setShowHint(true)}><EyeIcon /> Podpowiedź</button>
        )}

        {showHint && (
          <div className="hint"><strong>Podpowiedź</strong>{currentWord.de}</div>
        )}

        <button onClick={() => setIsHardMode(prev => !prev)} className="hard-mode-toggle">
          {isHardMode ? 'Hard' : 'Normal'}
        </button>
      </div>
      <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
    </>
  );
}
